import React, { useEffect, useState, useCallback } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import AdminUserProfile from "./AdminUserProfile";

const PROMOTER_SUBTYPES = [
  { value: "type_promoter_pr", label: "PR" },
  { value: "type_promoter_expert", label: "Expert" },
  { value: "type_promoter_hybrid", label: "Hybrid" },
];

const AUTO_ACTION_OPTIONS = [
  { key: "all", label: "All" },
  { key: "manual_review", label: "Manual Review" },
  { key: "ghost", label: "Ghost" },
  { key: "no_override", label: "No Override" },
];

function AdminUserProfiles() {
  const [searchInput, setSearchInput] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [userType, setUserType] = useState("all"); // "all", "media", "promoter", "admin"
  const [promoterSubtypes, setPromoterSubtypes] = useState({
    type_promoter_pr: true,
    type_promoter_expert: true,
    type_promoter_hybrid: true,
  });
  const [teamFilter, setTeamFilter] = useState("all"); // "all", "has_team", "no_team"
  const [paymentFilter, setPaymentFilter] = useState("all"); // "all", "paid", "free"
  const [autoActionFilter, setAutoActionFilter] = useState("all"); // "all", "manual_review", "ghost", "no_override"
  const [showFilters, setShowFilters] = useState(false); // Toggle for showing/hiding filters
  const [profiles, setProfiles] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const paginationLimit = 30;

  const fetchProfiles = useCallback(
    async (append = false) => {
      if (append && loadingMore) return;
      if (append && !hasMore) return;

      if (append) {
        setLoadingMore(true);
      }

      const baseUrl = globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;
      const headers = new Headers(authenticatedHeadersHash());
      const offset = append ? profiles.length : 0;

      let queryString = `${baseUrl}/rest/v1/admin_user_profiles?limit=${paginationLimit}&offset=${offset}`;

      // Default sort by last_active_at desc when no search query
      if (!activeQuery) {
        queryString += "&order=last_active_at.desc.nullslast";
      }

      // Text search
      if (activeQuery) {
        const trimmedQuery = activeQuery.trim();
        const queryArray = trimmedQuery.split(/\s+/);
        const queryWithPrefixMatcher = `${queryArray.map((a) => a.trim()).join(":*&")}:*`;
        const encodedSearchQuery = encodeURIComponent(queryWithPrefixMatcher);
        queryString += `&tsv_content=fts(english).${encodedSearchQuery}`;
      }

      // User type and permissions filters (server-side)
      if (userType === "media") {
        queryString += "&type_of_profile=eq.type_media&permissions=lt.1";
      } else if (userType === "promoter") {
        queryString += "&permissions=lt.1";
        const selectedSubtypes = Object.entries(promoterSubtypes)
          .filter(([, isSelected]) => isSelected)
          .map(([subtype]) => subtype);

        if (selectedSubtypes.length === 0) {
          setProfiles([]);
          setHasMore(false);
          setLoadingMore(false);
          return;
        }
        if (selectedSubtypes.length === 3) {
          queryString += "&type_of_profile=like.type_promoter*";
        } else if (selectedSubtypes.length === 1) {
          queryString += `&type_of_profile=eq.${selectedSubtypes[0]}`;
        } else {
          queryString += `&type_of_profile=in.(${selectedSubtypes.join(",")})`;
        }
      } else if (userType === "admin") {
        queryString += "&permissions=gte.1";
      }

      // Team filter (server-side, promoter tab only)
      if (userType === "promoter") {
        if (teamFilter === "has_team") {
          queryString += "&team_name=not.is.null";
        } else if (teamFilter === "no_team") {
          queryString += "&team_name=is.null";
        }

        // Payment filter (server-side, promoter tab only)
        if (paymentFilter === "paid") {
          queryString += "&payment_plan=neq.free";
        } else if (paymentFilter === "free") {
          queryString += "&payment_plan=eq.free";
        }
      }

      // Auto-action filter (media tab only) — uses effective_profile_action (includes synthesized)
      if (userType === "media") {
        if (autoActionFilter === "manual_review") {
          queryString += "&effective_profile_action=eq.manual_review";
        } else if (autoActionFilter === "ghost") {
          queryString += "&effective_profile_action=eq.ghost";
        } else if (autoActionFilter === "no_override") {
          queryString += "&effective_profile_action=is.null";
        }
      }

      const res = await fetch(queryString, { headers });
      const data = await res.json();

      if (!Array.isArray(data)) {
        // eslint-disable-next-line no-console
        console.error("admin_user_profiles fetch returned non-array:", data);
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      if (data.length === 0 && !append) {
        setProfiles([]);
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      if (data.length === 0 && append) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      setProfiles((prev) => {
        if (!append) return data;

        const existingIds = new Set(prev.map((p) => p.profile_id));
        const newProfiles = data.filter((p) => !existingIds.has(p.profile_id));
        return [...prev, ...newProfiles];
      });
      setHasMore(data.length >= paginationLimit);
      setLoadingMore(false);
    },
    // Intentionally omits loadingMore, hasMore, profiles from deps to prevent recreation on every state change.
    // Using stale values for early-return guards (lines 38-39) is acceptable - prevents redundant fetches.
    // profiles.length (line 47) may be stale but pagination offset drift is handled by server-side limits.
    // setProfiles uses functional update (line 142) to access current value, avoiding stale state bugs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeQuery, userType, promoterSubtypes, teamFilter, paymentFilter, autoActionFilter],
  );

  useEffect(() => {
    setProfiles([]);
    setHasMore(true);
    fetchProfiles(false);
    // Omits fetchProfiles - it's tied to the same filter deps listed above. Explicit filter deps make intent clearer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery, userType, promoterSubtypes, teamFilter, paymentFilter, autoActionFilter]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loadingMore) {
          fetchProfiles(true);
        }
      },
      { threshold: 0.1 },
    );

    const sentinel = document.getElementById("scroll-sentinel");
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, loadingMore, fetchProfiles]);

  // Type-ahead search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setActiveQuery(searchInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleReset = () => {
    setSearchInput("");
    setUserType("all");
    setPromoterSubtypes({
      type_promoter_pr: true,
      type_promoter_expert: true,
      type_promoter_hybrid: true,
    });
    setTeamFilter("all");
    setPaymentFilter("all");
    setAutoActionFilter("all");
  };

  const handleUserTypeChange = (newType) => {
    setUserType(newType);
    // Reset auto-action filter when switching away from media tab
    if (newType !== "media") {
      setAutoActionFilter("all");
    }
  };

  const hasActiveFilters =
    searchInput || userType !== "all" || teamFilter !== "all" || paymentFilter !== "all" || autoActionFilter !== "all";

  const handleSubtypeToggle = (subtype) => {
    setPromoterSubtypes((prev) => ({
      ...prev,
      [subtype]: !prev[subtype],
    }));
  };

  return (
    <div>
      {/* Search bar and filter controls */}
      <div className="d-flex align-items-center gap-2 mb-3">
        {/* Search input - plain, type-ahead */}
        <input
          type="text"
          className="form-control"
          placeholder="Search users..."
          style={{ width: "400px" }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        {/* Filters toggle button */}
        <button
          type="button"
          className={`btn ${showFilters ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <i className="fa fa-sliders me-1" />
          {showFilters ? "Hide Filters" : "Filters"}
        </button>

        {/* Reset button - only show when filters are active */}
        {hasActiveFilters && (
          <button type="button" className="btn btn-outline-secondary" onClick={handleReset}>
            <i className="fa fa-refresh me-1" />
            Reset
          </button>
        )}

        {/* Metabase dashboard link */}
        <a
          href="https://qwoted.metabaseapp.com/dashboard/14?tab=45-active-users"
          className="btn btn-sm btn-link text-muted ms-auto"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="fa fa-chart-line me-1" />
          Active Users Dashboard
        </a>
      </div>

      {/* Collapsible filters section */}
      {showFilters && (
        <div className="mb-2 bg-light rounded">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {/* User type radio buttons */}
            <div className="btn-group" role="group" aria-label="User type filter">
              <input
                type="radio"
                className="btn-check"
                name="userType"
                id="userTypeAll"
                checked={userType === "all"}
                onChange={() => handleUserTypeChange("all")}
              />
              <label className="btn btn-outline-secondary btn-sm" htmlFor="userTypeAll">
                All
              </label>

              <input
                type="radio"
                className="btn-check"
                name="userType"
                id="userTypeMedia"
                checked={userType === "media"}
                onChange={() => handleUserTypeChange("media")}
              />
              <label className="btn btn-outline-secondary btn-sm" htmlFor="userTypeMedia">
                Media
              </label>

              <input
                type="radio"
                className="btn-check"
                name="userType"
                id="userTypePromoter"
                checked={userType === "promoter"}
                onChange={() => handleUserTypeChange("promoter")}
              />
              <label className="btn btn-outline-secondary btn-sm" htmlFor="userTypePromoter">
                Promoter
              </label>

              <input
                type="radio"
                className="btn-check"
                name="userType"
                id="userTypeAdmin"
                checked={userType === "admin"}
                onChange={() => handleUserTypeChange("admin")}
              />
              <label className="btn btn-outline-secondary btn-sm" htmlFor="userTypeAdmin">
                Admin
              </label>
            </div>

            {/* Promoter subtype checkboxes - only shown when Promoter is selected */}
            {userType === "promoter" && (
              <>
                <div className="d-flex align-items-center gap-2">
                  {PROMOTER_SUBTYPES.map((subtype) => (
                    <div className="form-check form-check-inline mb-0" key={subtype.value}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={subtype.value}
                        checked={promoterSubtypes[subtype.value]}
                        onChange={() => handleSubtypeToggle(subtype.value)}
                      />
                      <label className="form-check-label" htmlFor={subtype.value}>
                        {subtype.label}
                      </label>
                    </div>
                  ))}
                </div>

                {/* Team filter */}
                <div className="d-flex align-items-center gap-2">
                  <label htmlFor="team-filter" className="form-label mb-0 text-muted small">
                    Team:
                  </label>
                  <select
                    id="team-filter"
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={teamFilter}
                    onChange={(e) => setTeamFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="has_team">Has Team</option>
                    <option value="no_team">No Team</option>
                  </select>
                </div>

                {/* Payment filter */}
                <div className="d-flex align-items-center gap-2">
                  <label htmlFor="payment-filter" className="form-label mb-0 text-muted small">
                    Payment:
                  </label>
                  <select
                    id="payment-filter"
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={paymentFilter}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="free">Free</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Auto-action filter bar - media tab only */}
      {userType === "media" && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">Auto Action:</span>
          <div className="btn-group" role="group" aria-label="Auto action filter">
            {AUTO_ACTION_OPTIONS.map((opt) => (
              <React.Fragment key={opt.key}>
                <input
                  type="radio"
                  className="btn-check"
                  name="autoActionFilter"
                  id={`autoAction-${opt.key}`}
                  checked={autoActionFilter === opt.key}
                  onChange={() => setAutoActionFilter(opt.key)}
                />
                <label className="btn btn-outline-secondary btn-sm" htmlFor={`autoAction-${opt.key}`}>
                  {opt.label}
                </label>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      <table className="table table-hover">
        <thead className="table-light">
          <tr>
            <th className="fw-semibold">User</th>
            <th className="fw-semibold">Details</th>
            <th className="fw-semibold">Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((profile) => (
            <AdminUserProfile key={profile.profile_id} profile={profile} userType={userType} />
          ))}
        </tbody>
      </table>

      {/* Loading indicator */}
      {loadingMore && (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="text-muted">Loading more users...</span>
        </div>
      )}

      {/* Sentinel element for intersection observer */}
      {hasMore && <div id="scroll-sentinel" style={{ height: "1px" }} />}

      {/* End of results message */}
      {!hasMore && profiles.length > 0 && (
        <div className="text-center py-4 text-muted">
          <small>Showing all {profiles.length} users</small>
        </div>
      )}
    </div>
  );
}

export default AdminUserProfiles;
